import React, { JSX } from 'react'
import { AnalyticsMetafield, CustomerTotals } from 'types/api'
import { addCustomerName, cleanVariables, graphqlRequest } from 'lib/helpers'
import { RefreshingBlock } from './Loading'
import getTopCustomerData from '../graphql/getTopCustomerData.graphql'
import getTopCustomerDataAdmin from '../graphql/getTopCustomerDataAdmin.graphql'

import { useQuery } from '@tanstack/react-query'
import { Leaderboard } from './Leaderboard'

export interface TopCustomersLeaderboardProps {
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  client: AnalyticsMetafield | null
  role?: string
}

export const TopCustomerData = ({ token, client, filters, role }: TopCustomersLeaderboardProps): JSX.Element => {
  const customerDataAllowed = role === 'admin' || role === 'analyst'
  const query = customerDataAllowed ? getTopCustomerDataAdmin : getTopCustomerData

  const {
    data,
    isFetching
  } = useQuery({
    queryKey: ['dashboard', 'topCustomers', filters, client?.id],

    queryFn: async () => {
      if (token == null || client == null) return

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filters?.productOption?.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate,
        pageSize: 10,
        offset: 0,
        sort: 'sales',
        sortDirection: 'desc'
      })
      const { customers } = await graphqlRequest<{ customers: CustomerTotals[] }>(
        query, cleanVar, token
      )

      const customersWithNames = addCustomerName(customers, client.title)

      return customersWithNames
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  return (
    <div className='relative shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col'>
      {isFetching && RefreshingBlock}
      <Leaderboard
        noDataMessage='No customer data for specified filters'
        hoverData={customerDataAllowed}
        hoverColumns={[{ colName: 'customerFirst', displayName: 'First Name' }, { colName: 'customerLast', displayName: 'Last Name' }, { colName: 'email', displayName: 'Email' }, { colName: 'smsMarketingConsent', displayName: 'SMS Marketing Consent' }, { colName: 'emailMarketingConsent', displayName: 'Email Marketing Consent' }]}
        idField='customerId'
        data={data ?? []}
        defaultSortKey='sales'
        columns={['customerName', 'sales']}
        title='Top customers by amount spent'
        limit={8}
      />
    </div>
  )
}
